import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, SideNav, SideNavGroup, SideNavItem, Tooltip } from '@origo/origo-ui';
import {
    NoteIcon,
    ListChecksIcon,
    GitBranchIcon,
    FileTextIcon,
    ScalesIcon,
    ArrowLeftIcon,
} from '@phosphor-icons/react';
import styles from './PlanDetailShell.module.css';

export type PlanTab = 'kart' | 'info' | 'behandlinger' | 'planforhold' | 'dokumenter' | 'dispensasjoner';

interface PlanDetailShellProps {
    activeTab: PlanTab;
    onTabChange: (tab: PlanTab) => void;
    onBackToMap: () => void;
    /** Plannavn vist i topp-raden over SideNav. */
    planTitle?: string;
    identifiers?: { nasjonalArealplanId: string; planId: string; admomrkode: string };
    /** Planen gjelder ikke lenger — vises som chip ved plannavnet (AK 6.6). */
    ikkeGjeldende?: boolean;
    /** Handlinger for hele planen, høyrejustert i topp-raden (f.eks. «…»-menyen). */
    actions?: React.ReactNode;
    /** Varsel for hele planen mellom topp-raden og fanene, synlig på alle faner. */
    notice?: React.ReactNode;
    children: React.ReactNode;
}

const PlanDetailShell: React.FC<PlanDetailShellProps> = ({
    activeTab,
    onTabChange,
    onBackToMap,
    planTitle,
    identifiers,
    ikkeGjeldende,
    actions,
    notice,
    children,
}) => {
    const { t } = useTranslation();

    const handleSelect = (id: string) => {
        if (id === 'kart') {
            onBackToMap();
            return;
        }
        onTabChange(id as PlanTab);
    };

    return (
        <div className={styles.shell}>
            <a href="#planregister-content" className={styles.skipLink}>
                {t('common.skipToContent')}
            </a>
            <div className={styles.topbar}>
                <div className={styles.topbarLeft}>
                    <button type="button" className={styles.backLink} onClick={onBackToMap}>
                        <ArrowLeftIcon size={16} weight="duotone" aria-hidden />
                        <span>{t('planDetail.backToSearch')}</span>
                    </button>
                    {planTitle && (
                        <span className={styles.planTitle} title={planTitle}>
                            {planTitle}
                        </span>
                    )}
                    {ikkeGjeldende && (
                        <Chip variant="warning" className={styles.statusChip}>{t('planValidity.notCurrent')}</Chip>
                    )}
                </div>
                <div className={styles.topbarRight}>
                    {identifiers && (
                        <Tooltip
                            content={t('planDetail.identifierTooltip', {
                                admomrkode: identifiers.admomrkode,
                                planId: identifiers.planId,
                            })}
                            position="bottom"
                        >
                            {/* Fokuserbar så tooltipen også nås med tastatur. */}
                            <span className={styles.identifier} tabIndex={0}>
                                <span className={styles.identifierLabel}>{t('planDetail.nasjonalArealplanId')}</span>
                                <span className={styles.identifierValue}>{identifiers.nasjonalArealplanId}</span>
                            </span>
                        </Tooltip>
                    )}
                    {actions}
                </div>
            </div>
            {notice}
            <div className={styles.body}>
                <aside className={styles.aside}>
                    <SideNav activeId={activeTab} onSelect={handleSelect}>
                        <SideNavGroup>
                            <SideNavItem id="info" icon={<NoteIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.info')}
                            </SideNavItem>
                            <SideNavItem id="behandlinger" icon={<ListChecksIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.behandlinger')}
                            </SideNavItem>
                            <SideNavItem id="planforhold" icon={<GitBranchIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.planforhold')}
                            </SideNavItem>
                            <SideNavItem id="dokumenter" icon={<FileTextIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.dokumenter')}
                            </SideNavItem>
                            <SideNavItem id="dispensasjoner" icon={<ScalesIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.dispensasjoner')}
                            </SideNavItem>
                        </SideNavGroup>
                    </SideNav>
                </aside>
                <main id="planregister-content" className={styles.content} tabIndex={-1}>
                    {children}
                </main>
            </div>
        </div>
    );
};

export default PlanDetailShell;
